(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const createAutoplay = (root, advance, minimum = 3000, pauseOnInteraction = true) => {
    let timer;
    let hovered = false;
    let focused = false;
    let visible = false;
    const interval = Math.max(minimum, Number(root.dataset.interval) || 7000);
    const stop = () => { window.clearTimeout(timer); timer = undefined; };
    const schedule = () => {
      stop();
      const paused = !visible || reducedMotion.matches || (pauseOnInteraction && (hovered || focused)) || document.hidden;
      root.classList.toggle('is-paused', paused);
      if (root.dataset.autoplay !== 'true' || paused) return;
      timer = window.setTimeout(() => { advance(); schedule(); }, interval);
    };
    root.addEventListener('mouseenter', () => { hovered = true; schedule(); });
    root.addEventListener('mouseleave', () => { hovered = false; schedule(); });
    root.addEventListener('focusin', () => { focused = true; schedule(); });
    root.addEventListener('focusout', event => {
      if (!root.contains(event.relatedTarget)) { focused = false; schedule(); }
    });
    document.addEventListener('visibilitychange', schedule);
    reducedMotion.addEventListener('change', schedule);
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      schedule();
    });
    observer.observe(root);
    schedule();
    return { restart: schedule };
  };

  function initCapabilities(root) {
    if (root.dataset.absenReady) return;
    root.dataset.absenReady = 'true';
    const tabs = [...root.querySelectorAll('[data-cap-tab]')];
    const panels = [...root.querySelectorAll('[data-cap-panel]')];
    const steps = [...root.querySelectorAll('[data-cap-step]')];
    let active;
    let autoplay;
    const show = name => {
      if (active === name || !tabs.some(tab => tab.dataset.capTab === name)) return;
      active = name;
      tabs.forEach(tab => {
        const selected = tab.dataset.capTab === name;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      panels.forEach(panel => { panel.hidden = panel.dataset.capPanel !== name; });
      steps.forEach(step => {
        const selected = step.dataset.capStep === name;
        step.classList.toggle('is-active', selected);
        const fill = step.querySelector('[data-cap-progress-fill]');
        if (fill) {
          fill.style.animationDuration = `${Math.max(5000, Number(root.dataset.interval) || 7000)}ms`;
          if (selected && !reducedMotion.matches) { fill.style.animationName = 'none'; fill.offsetWidth; fill.style.animationName = ''; }
        }
      });
    };
    autoplay = createAutoplay(root, () => {
      const next = (tabs.findIndex(tab => tab.dataset.capTab === active) + 1) % tabs.length;
      if (tabs.length) show(tabs[next].dataset.capTab);
    }, 5000);
    tabs.forEach((tab, index) => {
      tab.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse') show(tab.dataset.capTab);
      });
      tab.addEventListener('click', () => { show(tab.dataset.capTab); autoplay.restart(); });
      tab.addEventListener('keydown', event => {
        const delta = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (delta) {
          event.preventDefault();
          const next = tabs[(index + delta + tabs.length) % tabs.length];
          show(next.dataset.capTab); next.focus(); autoplay.restart();
        }
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); show(tab.dataset.capTab); autoplay.restart(); }
      });
    });
    show('fast');
  }

  function initProducts(root) {
    if (root.dataset.absenReady) return;
    root.dataset.absenReady = 'true';
    const tabs = [...root.querySelectorAll('[data-product-tab]')];
    const cards = [...root.querySelectorAll('[data-product-category]')];
    const empty = root.querySelector('[data-product-empty]');
    const show = category => {
      tabs.forEach(item => item.setAttribute('aria-selected', String(item.dataset.productTab === category)));
      cards.forEach(card => { card.hidden = card.dataset.productCategory !== category; });
      if (empty) empty.hidden = cards.some(card => card.dataset.productCategory === category);
    };
    tabs.forEach(tab => tab.addEventListener('click', () => show(tab.dataset.productTab)));
    show('repair');
  }

  function initLifecycle(root) {
    if (root.dataset.absenReady) return;
    root.dataset.absenReady = 'true';
    const tabs = [...root.querySelectorAll('[data-lifecycle-tab]')];
    const panels = [...root.querySelectorAll('[data-lifecycle-panel]')];
    let active = tabs[0]?.dataset.lifecycleTab;
    let autoplay;
    const showPath = name => {
      active = name;
      tabs.forEach(tab => {
        const selected = tab.dataset.lifecycleTab === name;
        tab.setAttribute('aria-selected', String(selected)); tab.tabIndex = selected ? 0 : -1;
      });
      panels.forEach(panel => { panel.hidden = panel.dataset.lifecyclePanel !== name; });
    };
    autoplay = createAutoplay(root, () => {
      if (tabs.length > 1) showPath(tabs.find(tab => tab.dataset.lifecycleTab !== active)?.dataset.lifecycleTab);
    }, 3000, false);
    tabs.forEach(tab => tab.addEventListener('click', () => { showPath(tab.dataset.lifecycleTab); autoplay.restart(); }));
    root.querySelectorAll('[data-lifecycle-panel]').forEach(panel => {
      const steps = [...panel.querySelectorAll('[data-lifecycle-step]')];
      const dots = [...panel.querySelectorAll('[data-lifecycle-dot]')];
      dots.forEach(dot => dot.addEventListener('click', () => {
        const index = Number(dot.dataset.lifecycleIndex);
        steps.forEach((step, i) => step.classList.toggle('is-active', i === index));
        dots.forEach((item, i) => { item.classList.toggle('is-active', i === index); item.setAttribute('aria-pressed', String(i === index)); });
        steps[index]?.scrollIntoView({behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'center'});
        autoplay.restart();
      }));
    });
    showPath(active);
  }

  function initProfessionalMedia(root) {
    if (root.dataset.absenMediaReady) return;
    root.dataset.absenMediaReady = 'true';
    const slides = [...root.querySelectorAll('[data-professional-slide]')];
    const dots = [...root.querySelectorAll('[data-professional-dot]')];
    const previous = root.querySelector('[data-professional-prev]');
    const next = root.querySelector('[data-professional-next]');
    if (slides.length < 2) return;
    root.dataset.autoplay = 'true';
    let active = 0;
    let autoplay;
    const show = index => {
      active = (index + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        slide.hidden = i !== active;
        slide.classList.toggle('is-active', i === active);
      });
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === active);
        dot.setAttribute('aria-current', String(i === active));
      });
    };
    autoplay = createAutoplay(root, () => show(active + 1), 3500, false);
    dots.forEach(dot => dot.addEventListener('click', () => {
      show(Number(dot.dataset.professionalDot));
      autoplay.restart();
    }));
    previous?.addEventListener('click', () => { show(active - 1); autoplay.restart(); });
    next?.addEventListener('click', () => { show(active + 1); autoplay.restart(); });
    show(0);
  }

  function initScenarios(root) {
    if (root.dataset.absenReady) return;
    root.dataset.absenReady = 'true';
    const slides = [...root.querySelectorAll('[data-scenario-slide]')];
    const cards = [...root.querySelectorAll('[data-scenario-card]')];
    const triggers = [...root.querySelectorAll('[data-scenario-trigger]')];
    const dots = [...root.querySelectorAll('[data-scenario-dot]')];
    let active = 0;
    let autoplay;
    const show = index => {
      if (!slides.length) return;
      active = (index + slides.length) % slides.length;
      slides.forEach((slide, i) => { slide.hidden = i !== active; slide.classList.toggle('is-active', i === active); });
      cards.forEach((card, i) => card.classList.toggle('is-active', i === active));
      triggers.forEach((trigger, i) => { trigger.setAttribute(trigger.tagName === 'A' ? 'aria-current' : 'aria-pressed', String(i === active)); });
      dots.forEach((dot, i) => { dot.classList.toggle('is-active', i === active); dot.setAttribute('aria-pressed', String(i === active)); });
    };
    autoplay = createAutoplay(root, () => show(active + 1));
    triggers.forEach(trigger => {
      const index = Number(trigger.dataset.scenarioIndex);
      trigger.addEventListener('click', () => { show(index); autoplay.restart(); });
    });
    dots.forEach(dot => dot.addEventListener('click', () => { show(Number(dot.dataset.scenarioIndex)); autoplay.restart(); }));
    show(0);
  }

  function initTestimonials(root) {
    if (root.dataset.absenReady) return;
    root.dataset.absenReady = 'true';
    const slides = [...root.querySelectorAll('.absen-services-testimonials__slide')];
    let current = 0;
    const show = index => { if (!slides.length) return; current = (index + slides.length) % slides.length; slides.forEach((slide, i) => { slide.hidden = i !== current; }); };
    root.querySelector('[data-testimonial-prev]')?.addEventListener('click', () => show(current - 1));
    root.querySelector('[data-testimonial-next]')?.addEventListener('click', () => show(current + 1));
  }

  const init = scope => {
    scope.querySelectorAll('[data-absen-capabilities]').forEach(initCapabilities);
    scope.querySelectorAll('[data-professional-media]').forEach(initProfessionalMedia);
    scope.querySelectorAll('[data-absen-products]').forEach(initProducts);
    scope.querySelectorAll('[data-absen-lifecycle]').forEach(initLifecycle);
    scope.querySelectorAll('[data-absen-scenarios]').forEach(initScenarios);
    scope.querySelectorAll('[data-absen-testimonials]').forEach(initTestimonials);
    scope.querySelectorAll('[data-show-service-standards]').forEach(link => {
      if (link.dataset.absenReady) return;
      link.dataset.absenReady = 'true';
      link.addEventListener('click', () => {
        const delivery = document.querySelector('[data-lifecycle-tab="delivery"]');
        window.setTimeout(() => delivery?.click(), 50);
      });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document)); else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
